import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import styles from './styles.module.scss';
import { PrimitiveProps } from '@helpers/interfaces/primitive';
import classnames from 'classnames';

export interface CircularRangeProps extends PrimitiveProps {
	activeLabel?: string;
	enableMin?: boolean;
	end?: number;
	haptic?: string;
	indices?: number;
	labels?: string;
	max?: number;
	min?: number;
	step?: number;
	shiftStep?: number;
	start?: number;
	suffix?: string;
	value?: number;
	defaultText?: string;
	supplementalText?: string;
	onChange?: (value: number) => void;
	onInput?: (value: number) => void;
	setElementRef?: (element: HTMLDivElement) => void;
}

export interface CircularRangeRef {
	setValue: (value: number) => void;
	getValue: () => number;
}

const CircularRange = forwardRef<CircularRangeRef, CircularRangeProps>(({
	activeLabel,
	className,
	enableMin = true,
	end = 360,
	haptic = '',
	indices = 0,
	labels = '',
	max = 100,
	min = 0,
	step = 1,
	shiftStep,
	start = 0,
	suffix = '',
	value = 0,
	defaultText,
	supplementalText,
	onChange,
	onInput,
	setElementRef,
	...props
}, ref) => {
	const [currentValue, setCurrentValue] = useState(value);
	const [, setSupplementalTextValue] = useState(supplementalText);
	const [isDragging, setIsDragging] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const lastValueRef = useRef(value);
	const justFinishedDraggingRef = useRef(false);
	const isSafariRef = useRef(false);

	// Detect Safari (exclude Chrome, Edge, Opera, Firefox, iOS Chrome)
	useEffect(() => {
		if (typeof window === 'undefined') return;
		const ua = navigator.userAgent;
		isSafariRef.current = /Safari\//.test(ua) && !/(Chrome|Chromium|Edg|OPR|OPiOS|CriOS|FxiOS)/.test(ua);
	}, []);

	// Parse haptic values
	const hapticValues = React.useMemo(() => {
		return haptic.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v));
	}, [haptic]);

	// Parse labels
	const parsedLabels = React.useMemo(() => {
		if (!labels) return [];
		return labels.split(',').map(label => {
			const [value, text] = label.split(':');
			return { value: parseInt(value), text: text || value };
		});
	}, [labels]);

	// Calculate CSS custom properties
	const cssVars = React.useMemo(() => {
		const range = max - min;
		const angleRange = end - start;
		const fillPercentage = range > 0 ? (currentValue - min) / range : 0;
		const fillAngle = start + (fillPercentage * angleRange);

		return {
			'--_value': currentValue,
			'--_fill': fillAngle,
			'--_start': start,
			'--_end': end,
			'--_tb': `${(start / 360) * 100}%`,
			'--_ta': `${(end / 360) * 100}%`,
		};
	}, [currentValue, min, max, start, end]);

	// Helper to apply step and clamp
	const applyStepClamp = useCallback((raw: number) => {
		const effectiveStep = step || 1;
		const stepped = Math.round((raw - min) / effectiveStep) * effectiveStep + min;
		return Math.max(min, Math.min(max, stepped));
	}, [min, max, step]);

	// Safari repaint workaround
	const safariRepaint = useCallback(() => {
		if (!isSafariRef.current) return;
		const el = containerRef.current;
		if (!el) return;
		// Force layout and invalidate styles so offset-path positions update
		void el.getBoundingClientRect();
		el.style.setProperty('--_invalidate', String(performance.now()));
	}, []);

	// Initial mount: slight delay before first paint to stabilize Safari
	useEffect(() => {
		if (!isSafariRef.current) return;
		const id = setTimeout(() => {
			safariRepaint();
		}, 0);
		const visHandler = () => { if (!document.hidden) safariRepaint(); };
		const pageShowHandler = () => { safariRepaint(); };
		document.addEventListener('visibilitychange', visHandler);
		window.addEventListener('pageshow', pageShowHandler);
		return () => {
			clearTimeout(id);
			document.removeEventListener('visibilitychange', visHandler);
			window.removeEventListener('pageshow', pageShowHandler);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Repaint Safari when core inputs change
	useEffect(() => {
		safariRepaint();
	}, [currentValue, start, end, safariRepaint]);

	// Handle pointer events
	const handlePointerDown = useCallback((e: React.PointerEvent) => {
		setIsDragging(true);
		lastValueRef.current = currentValue;
		e.currentTarget.setPointerCapture(e.pointerId);
	}, [currentValue]);

	// Handle clicks on the track to move thumb to position
	const handleClick = useCallback((e: React.MouseEvent) => {
		if (!containerRef.current) return;

		const rect = containerRef.current.getBoundingClientRect();
		const centerX = rect.width / 2;
		const centerY = rect.height / 2;
		const x = e.clientX - rect.left - centerX;
		const y = e.clientY - rect.top - centerY;

		const degree = (((Math.atan2(y, x) * 180 / Math.PI) + 90 + 360) % 360);
		const relativeDegree = (degree - start + 360) % 360;
		const angleRange = end - start;
		const range = max - min;
		const radian = angleRange / range;
		let value = (relativeDegree / radian) + min;

		if (angleRange < 360) {
			if (relativeDegree > angleRange) {
				const midGap = (360 - angleRange) / 2;
				const newValue = relativeDegree - angleRange < midGap ? max : min;
				setCurrentValue(newValue);
				onChange?.(newValue);
				return;
			}
		} else {
			if (Math.abs(value - lastValueRef.current) > range / 2) {
				value = (value > lastValueRef.current) ? min : max;
			}
		}

		const clampedValue = applyStepClamp(value);
		setCurrentValue(clampedValue);
		onChange?.(clampedValue);
		lastValueRef.current = clampedValue;

		if (hapticValues.includes(clampedValue) && navigator.vibrate) {
			navigator.vibrate(10);
		}
	}, [start, end, min, max, onChange, hapticValues, applyStepClamp]);

	const handlePointerMove = useCallback((e: React.PointerEvent) => {
		if (!isDragging || !containerRef.current) return;

		const rect = containerRef.current.getBoundingClientRect();
		const centerX = rect.width / 2;
		const centerY = rect.height / 2;
		const x = e.clientX - rect.left - centerX;
		const y = e.clientY - rect.top - centerY;

		const degree = (((Math.atan2(y, x) * 180 / Math.PI) + 90 + 360) % 360);
		const relativeDegree = (degree - start + 360) % 360;
		const angleRange = end - start;
		const range = max - min;
		const radian = angleRange / range;
		let value = (relativeDegree / radian) + min;

		if (angleRange < 360) {
			if (relativeDegree > angleRange) {
				const midGap = (360 - angleRange) / 2;
				const newValue = relativeDegree - angleRange < midGap ? max : min;
				setCurrentValue(newValue);
				onInput?.(newValue);
				return;
			}
		} else {
			if (Math.abs(value - lastValueRef.current) > range / 2) {
				value = (value > lastValueRef.current) ? min : max;
			}
		}

		const clampedValue = applyStepClamp(value);
		setCurrentValue(clampedValue);
		onInput?.(clampedValue);
		lastValueRef.current = value;

		if (hapticValues.includes(clampedValue) && navigator.vibrate) {
			navigator.vibrate(10);
		}
	}, [isDragging, start, end, min, max, onInput, hapticValues, applyStepClamp]);

	const handlePointerUp = useCallback((e: React.PointerEvent) => {
		setIsDragging(false);
		justFinishedDraggingRef.current = true;
		e.currentTarget.releasePointerCapture(e.pointerId);
		onChange?.(currentValue);
		// Reset the flag after a short delay to allow parent to process
		setTimeout(() => {
			justFinishedDraggingRef.current = false;
		}, 50);
	}, [currentValue, onChange]);

	// Handle keyboard events
	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

		e.preventDefault();
		const effectiveStep = e.shiftKey ? (shiftStep ?? step) : (step || 1);
		const newValue = currentValue + ((e.key === 'ArrowUp' || e.key === 'ArrowRight') ? effectiveStep : -effectiveStep);
		const clampedValue = applyStepClamp(newValue);
		setCurrentValue(clampedValue);
		onChange?.(clampedValue);
	}, [currentValue, shiftStep, onChange, applyStepClamp]);

	// Update value when prop changes (but not while dragging or just finished dragging)
	useEffect(() => {
		if (!isDragging && !justFinishedDraggingRef.current) {
			setCurrentValue(value);
		}
		setSupplementalTextValue(supplementalText);
	}, [value, supplementalText, isDragging]);

	// Expose methods via ref
	useImperativeHandle(ref, () => ({
		setValue: (newValue: number) => {
			const clampedValue = Math.max(min, Math.min(max, newValue));
			setCurrentValue(clampedValue);
		},
		getValue: () => currentValue
	}), [currentValue, min, max]);

	// Generate indices
	const generateIndices = () => {
		if (indices === 0) return [];
		const startPercent = start / 360 * 100;
		const rangePercent = (end - start) / 360 * 100;
		const step = rangePercent / (indices - 1);

		return Array.from({ length: indices }, (_, i) => {
			const percent = startPercent + (i * step);
			return { percent };
		});
	};

	const indicesList = generateIndices();

	useEffect(() => {
		if (setElementRef && containerRef.current) {
			setElementRef(containerRef.current);
		}
	}, [setElementRef, containerRef.current]);

	return (
		<div
			ref={containerRef}
			className={classnames(styles.circularRange, className, {
				[styles.atMin]: enableMin && currentValue === min
			})}
			style={cssVars as React.CSSProperties}
			tabIndex={0}
			onKeyDown={handleKeyDown}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerUp}
			onClick={handleClick}
			role="slider"
			aria-valuemin={min}
			aria-valuemax={max}
			aria-valuenow={currentValue}
			aria-label="Circular range slider"
			{...props}
		>
			{/* Background layer */}
			<div className={styles.background} />

			{/* Track */}
			<div data-part="track" className={styles.track} />

			{/* Fill */}
			<div data-part="fill" className={styles.fill} />

			{/* Thumb */}
			<div data-part="thumb" className={styles.thumb} />

			{/* Indices */}
			<ul data-part="indices" className={styles.indices}>
				{indicesList.map((index, i) => (
					<li
						key={i}
						style={{ '--_p': `${index.percent}%` } as React.CSSProperties}
					/>
				))}
			</ul>

			{/* Labels */}
			<ol data-part="labels" className={styles.labels} aria-hidden="true">
				{parsedLabels.map((label, i) => {
					const isActive = label.value.toString() === activeLabel;
					const degree = ((label.value - min) * ((end - start) / (max - min))) + start;
					const percent = (degree / 360) * 100;

					return (
						<li
							key={i}
							value={label.value}
							data-part={`label-${label.value} ${isActive ? ' active-label' : ''}`.trim()}
							aria-readonly="true"
							style={{ '--_p': `${percent}%` } as React.CSSProperties}
							className={classnames({
								[styles.activeLabel]: isActive
							})}
						>
							{label.text}
						</li>
					);
				})}
			</ol>

			{/* Value display */}
			<div className={styles.valueDisplay}>
				{defaultText && currentValue === 0 && <h4>{defaultText}</h4>}
				{currentValue > 0 && <>
					<h4>{suffix}</h4>
					<h3>{Math.round(currentValue)}</h3>
					{/* {supplementalTextValue && <p>{supplementalTextValue}</p>} */}
				</>
				}
			</div>
		</div>
	);
});

CircularRange.displayName = 'CircularRange';

export { CircularRange }; 